import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { formatDate } from 'shared-utils';
import colors from '@/constants/Colors';

interface DateItem {
    date_id: number;
    date_started_at: string | Date | null;
    venue_name: string;
    venue_address_line_1: string;
    display_start_time?: string;
    display_end_time?: string;
    display_hours?: string;
}

interface DatePickerModalProps {
    visible: boolean;
    onClose: () => void;
    dates: DateItem[];
    currentDateId: number;
    onDateSelect: (dateId: number) => void;
}

const DatePickerModal: React.FC<DatePickerModalProps> = ({
    visible,
    onClose,
    dates,
    currentDateId,
    onDateSelect,
}) => {
    if (!visible) return null;

    return (
        <View style={styles.overlay}>
            <View style={styles.modalContent}>
                <View style={styles.container}>
                    <View style={styles.header}>
                        <View style={styles.headerTitleContainer}>
                            <Text style={styles.headerTitle}>All dates on job</Text>
                        </View>
                        <TouchableOpacity
                            style={styles.closeButton}
                            onPress={onClose}
                            activeOpacity={0.7}
                        >
                            <View style={styles.iconWrapper}>
                                <Ionicons name="close" size={22} color="#000" />
                            </View>
                        </TouchableOpacity>
                    </View>

                    <ScrollView style={styles.scrollView}>
                        <View style={styles.datesList}>
                            {dates.map((date) => {
                                const isCurrentDate = date.date_id === currentDateId;
                                const formattedDate = date.date_started_at
                                    ? formatDate(new Date(date.date_started_at), 'EEEE, do MMMM yyyy')
                                    : 'Date not available';

                                const timeDisplay = date.display_start_time && date.display_end_time
                                    ? `${date.display_start_time} - ${date.display_end_time} ${date.display_hours ? `(${date.display_hours})` : ''}`
                                    : '';

                                return (
                                    <TouchableOpacity
                                        key={date.date_id}
                                        style={[
                                            styles.dateItem,
                                            isCurrentDate && { backgroundColor: 'rgba(0, 0, 0, 0.05)' },
                                        ]}
                                        onPress={() => onDateSelect(date.date_id)}
                                        activeOpacity={0.7}
                                        disabled={isCurrentDate}
                                    >
                                        <View style={styles.dateItemContent}>
                                            <Text style={styles.dateText}>{formattedDate}</Text>

                                            {timeDisplay ? (
                                                <View style={styles.timeText}>
                                                    <Text>{timeDisplay}</Text>
                                                </View>
                                            ) : null}

                                            {date.venue_name ? (
                                                <Text style={styles.venueText}>{date.venue_name}</Text>
                                            ) : null}

                                            {date.venue_address_line_1 ? (
                                                <Text style={styles.addressText}>{date.venue_address_line_1}</Text>
                                            ) : null}
                                        </View>
                                    </TouchableOpacity>
                                );
                            })}
                        </View>
                    </ScrollView>
                </View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'flex-end',
        zIndex: 1000,
    },
    modalContent: {
        backgroundColor: 'white',
        borderTopLeftRadius: 15,
        borderTopRightRadius: 15,
        height: '70%',
        padding: 16,
    },
    container: {
        flex: 1,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
    },
    headerTitleContainer: {
        flex: 1,
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: 'bold',
    },
    iconWrapper: {
        padding: 5,
    },
    closeButton: {
        padding: 5,
    },
    scrollView: {
        flex: 1,
    },
    datesList: {
        flex: 1,
    },
    dateItem: {
        paddingVertical: 12,
        paddingHorizontal: 16,
        borderRadius: 8,
        marginBottom: 8,
    },
    dateItemContent: {
        gap: 4,
    },
    dateText: {
        fontSize: 16,
        fontWeight: '500',
    },
    timeText: {
        backgroundColor: '#f0f0f0',
        alignSelf: 'flex-start',
        paddingVertical: 4,
        paddingHorizontal: 8,
        borderRadius: 4,
        marginVertical: 2,
    },
    venueText: {
        fontSize: 15,
        marginTop: 2,
    },
    addressText: {
        fontSize: 14,
        color: '#666',
    },
});

export default DatePickerModal; 